import React, { useMemo } from "react"
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Text,
  Box,
  Heading,
  VStack,
  Input,
  FormControl,
  FormLabel,
  Divider,
  Stack,
} from "@chakra-ui/react"
import useProductTemplate from "@hooks/useProductTemplate"
import { useContent } from "@hooks/useContent"
import { Product } from "@usereactify/search/dist/src/types/graphql"
import { useStorage } from "@app/hooks/useCore"
import Table from "@app/components/Table"

interface ProductSizeFitDrawerProps {
  drawerType?: string
  isOpen: boolean
  tags?: string[] | undefined
  onClose: () => void
  handleDrawerType: (drawerType: string) => void
  handleOptions: () => void
  product: Product
}

type SizeProps = { [key: string]: string }
interface FitAssistantProps {
  fitAssistantContent: Array<{ [key: string]: any }>
  tags: [string]
  sizes: SizeProps
  // handleOptions: () => void
  product: Product
  onClose: () => void
}

const ProductSizeFitDrawer: React.FC<ProductSizeFitDrawerProps> = ({
  drawerType,
  isOpen,
  tags,
  onClose,
  handleDrawerType,
  handleOptions,
  product,
}) => {
  const {
    sizeGuide: { title, content, blocks, fitAssistantContent },
  } = useProductTemplate()
  const buttonProps = {
    width: `full`,
    py: 2,
    outline: 0,
    _focus: {
      outline: 0,
    },
    _hover: {
      textDecoration: `none`,
    },
    _active: {
      color: `black`,
      _after: {
        opacity: 1,
      },
    },
    _after: {
      content: `""`,
      width: `full`,
      height: `1px`,
      position: `absolute`,
      bottom: 0,
      left: 0,
      backgroundColor: `black`,
      opacity: 0,
      transition: `opacity 0.2s`,
    },
  }
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader p={0}>
          <Flex justifyContent="space-between" flexWrap="wrap" alignItems="center">
            <Box py={2} width={`100%`}>
              <Text fontSize="lg" width={`full`} textAlign={`center`}>
                {title}
              </Text>
            </Box>
            <HStack
              spacing={0}
              width={`full`}
              borderBottomWidth={`1px`}
              borderBottomColor={`brand.divider`}
              borderTopWidth={`1px`}
              borderTopColor={`brand.divider`}
            >
              <Button {...buttonProps} isActive={drawerType === "size-guide"} variant="text" onClick={() => handleDrawerType("size-guide")}>
                Size Chart
              </Button>
              <Button
                {...buttonProps}
                isActive={drawerType === "fit-assistant"}
                variant="text"
                onClick={() => handleDrawerType("fit-assistant")}
              >
                Fit Assistant
              </Button>
            </HStack>
          </Flex>
        </DrawerHeader>
        <DrawerBody p={4}>
          {drawerType === "size-guide" ? (
            <SizeGuide content={content} blocks={blocks} />
          ) : (
            <FitAssistant
              fitAssistantContent={fitAssistantContent}
              tags={tags}
              sizes={blocks}
              handleOptions={handleOptions}
              product={product}
              onClose={onClose}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

const SizeGuide = ({ content, blocks }: any) => {
  const { getContent } = useContent()
  const parsedContent = useMemo(() => getContent({ content }), [content, getContent])

  return (
    <Box>
      {content && (
        <Box py={2} textAlign={`center`}>
          {parsedContent}
        </Box>
      )}
      <VStack width={`100%`} spacing={4}>
        {blocks?.map(({ sizeHeading, sizeTable }) => (
          <Box key={sizeHeading?.toLowerCase()} width={`100%`}>
            {sizeHeading && (
              <Heading as={`h4`} size={`h4`} fontWeight={600} fontSize={`sm`} textAlign={`center`}>
                {sizeHeading}
              </Heading>
            )}
            <Table table={sizeTable} />
          </Box>
        ))}
      </VStack>
    </Box>
  )
}

const FitAssistant: React.FC<FitAssistantProps> = ({ fitAssistantContent, tags, sizes, product, onClose }) => {
  const { getContent } = useContent()
  const parsedContent = useMemo(() => getContent({ content: fitAssistantContent }), [fitAssistantContent, getContent])
  const [submitted, setSubmitted] = React.useState(false)
  const [childHeight, setChildHeight] = React.useState(0)
  const [childWeight, setChildWeight] = React.useState(0)
  const [suggestedSizes, setSuggestedSizes] = React.useState(null)

  const data = useMemo(() => {
    if (tags) {
      const sizeTag = tags.filter(tag => tag.indexOf("sizeguide:") != -1)
      if (sizeTag.length) {
        const list = sizes.filter(size => size.sizeHeading == sizeTag[0].split(":")[1])
        if (list.length) {
          return [list[0]]
        }
      }
    }

    const list = []
    sizes.map(size => {
      list.push(size)
    })
    return list
  }, [sizes, tags])

  const allSizes = useMemo(() => {
    const parsedSizes = []
    const relevant = data.filter(item => item.sizeHeading.toLowerCase().includes("clothing") || item.sizeHeading == "premie")

    relevant.map(item => {
      const keys = item?.sizeTable?.rows?.[0]?.cells

      const remainingRows = item?.sizeTable?.rows?.slice(1)

      remainingRows.map(row => {
        const data = row?.cells
        const rowObj = {}
        if (data && data.length && data[0]) {
          for (let j = 0; j < keys.length; j++) {
            rowObj[keys[j].toLowerCase()] = data[j]
          }
          const match = sizes.filter(size => size.size == rowObj.size)
          if (!(match && match.length)) {
            parsedSizes.push(rowObj)
          }
        }
      })
    })
    return parsedSizes
  }, [data, sizes])

  const getMatchingSizes = event => {
    event.preventDefault()

    const matchHeight: Array<{ [key: string]: string }> = []
    const matchWeight: Array<{ [key: string]: string }> = []
    const match = []
    if (childHeight) {
      allSizes.map(size => {
        if (size.height) {
          if (size.height.toLowerCase().indexOf("up to") != -1) {
            const thisHeight = size.height.toLowerCase().replace("up to", "").replace("cm", "").replace(" ", "")
            if (+childHeight < +thisHeight && +childHeight + 10 > +thisHeight) {
              matchWeight.push({
                size: size.size,
                weight: size.weight,
                height: size.height,
                reason: "Height",
              })
            }
          }
        }
      })
    }
    if (childWeight) {
      allSizes.map(size => {
        if (size.weight) {
          if (size.weight.toLowerCase().indexOf("up to") != -1) {
            const thisWeight = size.weight.toLowerCase().replace("up to", "").replace("kg", "").replace(" ", "")
            if (childWeight < thisWeight && childWeight + 1 > thisWeight) {
              matchHeight.push({
                size: size.size,
                weight: size.weight,
                height: size.height,
                reason: "Weight",
              })
            }
          } else if (size.weight.toLowerCase().indexOf("-") != -1) {
            const range = size.weight.toLowerCase().replace("kg", "").split("-")
            if (+range[0] <= +childWeight && +childWeight <= +range[1]) {
              matchHeight.push({
                size: size.size,
                weight: size.weight,
                height: size.height,
                reason: "Weight",
              })
            }
          }
        }
      })
    }
    if (matchHeight.length && matchWeight.length) {
      if (matchHeight[0].size == matchWeight[0].size) {
        matchWeight[0].reason = "Height & Weight"
        match.push(matchWeight[0])
      } else {
        match.push(matchHeight[0])
        match.push(matchWeight[0])
      }
    } else if (matchHeight.length) {
      match.push(matchHeight[0])
    } else if (matchWeight.length) {
      match.push(matchWeight[0])
    }
    setSuggestedSizes(match)
    setSubmitted(true)
  }

  //check if the product has available variant option
  const { setStorage } = useStorage()
  const productSizes = product?.options?.filter((option: { [key: string]: string }) => option.name === "Size")[0].values
  const isAvailable = (size: SizeProps) => {
    return productSizes.includes(size.size)
  }

  return (
    <Box>
      {fitAssistantContent && (
        <Box py={2} textAlign={`center`}>
          {parsedContent}
        </Box>
      )}
      <VStack as={`form`} onSubmit={event => getMatchingSizes(event)} mb={2} pb={2}>
        <FormControl name={`weight`}>
          <FormLabel fontSize={`xs`} fontWeight={600} marginBottom={1} color={`text.primary`}>
            WEIGHT (KG)
          </FormLabel>
          <Input type="number" px={2} onChange={e => setChildWeight(e.target.value)} />
        </FormControl>
        <FormControl name={`weight`}>
          <FormLabel fontSize={`xs`} fontWeight={600} marginBottom={1} color={`text.primary`}>
            HEIGHT (CM)
          </FormLabel>
          <Input type="number" px={2} onChange={e => setChildHeight(e.target.value)} />
        </FormControl>
        <Button type="submit" variant={`outline`} width={`full`}>
          Find Size
        </Button>
      </VStack>
      <Divider mb={2} />
      {submitted ? (
        <Stack divider={<Divider />}>
          {suggestedSizes && suggestedSizes.length ? (
            suggestedSizes.map((size: SizeProps, i: number) => {
              return (
                <Box key={i} my={1}>
                  <Heading as={`h4`} size={`h4`} fontWeight={600}>
                    Size:
                    {size.size}
                  </Heading>
                  <Text pb={2} fontSize={`xs`}>
                    Recommended Size based on {size.reason}
                  </Text>
                  <Text fontSize={`xs`}>Designed For</Text>
                  <Text fontSize={`xs`}>
                    Height:
                    {size.height}
                  </Text>
                  <Text pb={1} fontSize={`xs`}>
                    Weight:
                    {size.weight}
                  </Text>
                  {isAvailable(size) && (
                    <Button
                      onClick={() => {
                        setStorage("fit-assistant-size", size.size)
                        onClose()
                      }}
                      variant={"blue"}
                    >
                      Select This Size
                    </Button>
                  )}
                </Box>
              )
            })
          ) : (
            <Text fontSize={`sm`}>No suitable options found.</Text>
          )}
        </Stack>
      ) : null}
    </Box>
  )
}

export default ProductSizeFitDrawer
